/**
 * Fall runway merge-pickup tier weights — newest unlocked tier is rare;
 * lower tiers gain weight as run progress advances.
 */

import * as C from "../config/constants.js";

/**
 * @param {number} tierId
 * @param {number} runMaxTier currentGameMaxTier for this run
 * @returns {number} non-negative weight (0 = not spawnable)
 */
export function getTierSpawnWeight(tierId, runMaxTier) {
  var maxSpawnTier = C.RUN_SPAWN_MAX_TIER != null ? Number(C.RUN_SPAWN_MAX_TIER) : 6;
  var runMax = Math.max(0, Math.floor(Number(runMaxTier) || 0));
  var tier = Math.floor(Number(tierId) || 0);
  if (tier > runMax || tier >= maxSpawnTier) return 0;

  var base = C.TIER_SPAWN_WEIGHTS || [40, 30, 15, 8, 4, 2];
  var newWt = C.TIER_SPAWN_NEW_TIER_WEIGHT != null ? Number(C.TIER_SPAWN_NEW_TIER_WEIGHT) : 2;
  var boost = C.TIER_SPAWN_BOOST_PER_STEP != null ? Number(C.TIER_SPAWN_BOOST_PER_STEP) : 1.35;
  if (!isFinite(newWt) || newWt <= 0) newWt = 2;
  if (!isFinite(boost) || boost <= 0) boost = 1.35;

  if (tier === runMax) return newWt;

  var stepsBelow = runMax - tier;
  var b = base[tier] != null ? Number(base[tier]) : 1;
  if (!isFinite(b) || b <= 0) b = 1;
  return b * Math.pow(boost, stepsBelow);
}

/**
 * @param {Array<{ id: number }>} chain
 * @param {number} runMaxTier
 * @returns {Array<{ id: number }>}
 */
export function getEligibleSpawnTiers(chain, runMaxTier) {
  var maxSpawnTier = C.RUN_SPAWN_MAX_TIER != null ? Number(C.RUN_SPAWN_MAX_TIER) : 6;
  var runMax = Math.max(0, Math.floor(Number(runMaxTier) || 0));
  var list = chain || [];
  var eligible = [];
  for (var i = 0; i < list.length; i++) {
    var t = list[i];
    if (!t || t.id == null) continue;
    if (t.id !== 0 && t.id > runMax) continue;
    if (t.id >= maxSpawnTier) continue;
    eligible.push(t);
  }
  return eligible;
}

/**
 * @param {number} runMaxTier
 * @param {Array<{ id: number }>} chain
 * @param {() => number} [randomFn]
 * @returns {number} tier id
 */
export function pickSpawnTierFromRunMax(runMaxTier, chain, randomFn) {
  var eligible = getEligibleSpawnTiers(chain, runMaxTier);
  if (eligible.length === 0) return 0;
  var rand = typeof randomFn === "function" ? randomFn : Math.random;

  var sum = 0;
  var weights = [];
  for (var j = 0; j < eligible.length; j++) {
    var w = getTierSpawnWeight(eligible[j].id, runMaxTier);
    weights.push(w);
    sum += w;
  }
  if (sum <= 0) return eligible[0].id;

  var r = rand() * sum;
  for (var k = 0; k < eligible.length; k++) {
    r -= weights[k];
    if (r <= 0) return eligible[k].id;
  }
  return eligible[eligible.length - 1].id;
}

/**
 * Normalized spawn chances for eligible tiers (debug / tests).
 * @param {number} runMaxTier
 * @returns {Record<number, number>} tier id → percent 0–100
 */
export function getTierSpawnChanceTable(runMaxTier) {
  var chain = (typeof C.getActiveEvolutionChain === "function")
    ? C.getActiveEvolutionChain()
    : (C.EVOLUTION_CHAIN || []);
  var eligible = getEligibleSpawnTiers(chain, runMaxTier);
  var sum = 0;
  var raw = {};
  for (var i = 0; i < eligible.length; i++) {
    var id = eligible[i].id;
    var w = getTierSpawnWeight(id, runMaxTier);
    raw[id] = w;
    sum += w;
  }
  var out = {};
  if (sum <= 0) return out;
  for (var key in raw) {
    if (Object.prototype.hasOwnProperty.call(raw, key)) {
      out[key] = (raw[key] / sum) * 100;
    }
  }
  return out;
}